import type {
  AdminJob,
  AdminPlan,
  AdminSubscriptionListItem,
  AdminSubscriptionListQuery,
  PlanInput,
  RevenueSummary,
  AdminJobListItem,
  AdminJobListQuery,
  AdminOverview,
  AdminProvider,
  AdminProviderListItem,
  AdminProviderListQuery,
  AdminReport,
  AdminReportListItem,
  AdminReportListQuery,
  AdminStats,
  AdminUser,
  AdminUserListItem,
  AdminUserListQuery,
  AuditLogEntry,
  AuditLogQuery,
  DismissReportInput,
  EditJobInput,
  Paginated,
  ProviderDocumentInfo,
  ProviderStatusCounts,
  ResolveReportInput,
  UserStatusResult,
} from '@hemmafixa/shared';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { api } from '@/lib/api-client';

// Job review (P3-10), providers, users and the audit log (P2-10), reports (P5-06) and billing (P6-08) are real.

const reviewKey = ['admin', 'review'] as const;

type ReviewQueueParams = Partial<Pick<AdminJobListQuery, 'status' | 'q' | 'page' | 'pageSize'>>;

function queryString(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export function useReviewQueue(params: ReviewQueueParams) {
  return useQuery({
    queryKey: [...reviewKey, 'list', params],
    queryFn: () => api<Paginated<AdminJobListItem>>(`/admin/jobs${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

/** Jobs waiting for a decision, for the menu badge. Only fetched in the admin area. */
export function useReviewCount(enabled: boolean) {
  return useQuery({
    queryKey: [...reviewKey, 'count'],
    queryFn: () =>
      api<Paginated<AdminJobListItem>>(`/admin/jobs${queryString({ status: 'in_review', pageSize: 1 })}`),
    select: (result) => result.total,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useReviewJob(id: string) {
  return useQuery({
    queryKey: [...reviewKey, 'job', id],
    queryFn: () => orNull(api<AdminJob>(`/admin/jobs/${id}`)),
  });
}

/** One action on a job in review. The answer is the updated job; lists and the badge refresh. */
function useReviewAction<TInput>(id: string, request: (input: TInput) => Promise<AdminJob>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (job) => {
      queryClient.setQueryData([...reviewKey, 'job', id], job);
      // Refreshed in the background: the dialog closes as soon as the action is saved
      void queryClient.invalidateQueries({ queryKey: [...reviewKey, 'list'] });
      void queryClient.invalidateQueries({ queryKey: [...reviewKey, 'count'] });
    },
  });
}

export const useApproveJob = (id: string) =>
  useReviewAction<void>(id, () => api<AdminJob>(`/admin/jobs/${id}/approve`, { method: 'POST' }));

export const useRequestJobInfo = (id: string) =>
  useReviewAction<string[]>(id, (questions) =>
    api<AdminJob>(`/admin/jobs/${id}/request-info`, { method: 'POST', body: { questions } }),
  );

export const useRejectJob = (id: string) =>
  useReviewAction<string>(id, (reason) =>
    api<AdminJob>(`/admin/jobs/${id}/reject`, { method: 'POST', body: { reason } }),
  );

export const useEditJob = (id: string) =>
  useReviewAction<EditJobInput>(id, (input) =>
    api<AdminJob>(`/admin/jobs/${id}`, { method: 'PATCH', body: input }),
  );

/** A detail query that shows "not found" for a wrong or old link instead of an error with Try again. */
async function orNull<T>(request: Promise<T>) {
  try {
    return await request;
  } catch (error) {
    if (error instanceof ApiError && (error.status === 404 || error.status === 400)) return null;
    throw error;
  }
}

const providersKey = ['admin', 'providers'] as const;

type ProviderListParams = Partial<Pick<AdminProviderListQuery, 'status' | 'q' | 'page' | 'pageSize'>>;

export function useAdminProviders(params: ProviderListParams) {
  return useQuery({
    queryKey: [...providersKey, 'list', params],
    queryFn: () => api<Paginated<AdminProviderListItem>>(`/admin/providers${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

/** Profiles per status, for the filter chips, the overview and the menu badge. */
export function useProviderCounts(enabled = true) {
  return useQuery({
    queryKey: [...providersKey, 'counts'],
    queryFn: () => api<ProviderStatusCounts>('/admin/providers/counts'),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useAdminProvider(id: string) {
  return useQuery({
    queryKey: [...providersKey, 'one', id],
    queryFn: () => orNull(api<AdminProvider>(`/admin/providers/${id}`)),
  });
}

function useProviderDecision<TInput>(id: string, request: (input: TInput) => Promise<AdminProvider>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (provider) => {
      queryClient.setQueryData([...providersKey, 'one', id], provider);
      void queryClient.invalidateQueries({ queryKey: [...providersKey, 'list'] });
      void queryClient.invalidateQueries({ queryKey: [...providersKey, 'counts'] });
      void queryClient.invalidateQueries({ queryKey: auditKey });
    },
  });
}

export const useVerifyProvider = (id: string) =>
  useProviderDecision<void>(id, () =>
    api<AdminProvider>(`/admin/providers/${id}/verify`, { method: 'POST' }),
  );

export const useRejectProvider = (id: string) =>
  useProviderDecision<string>(id, (reason) =>
    api<AdminProvider>(`/admin/providers/${id}/reject`, { method: 'POST', body: { reason } }),
  );

const usersKey = ['admin', 'users'] as const;

type UserListParams = Partial<Pick<AdminUserListQuery, 'q' | 'role' | 'status' | 'page' | 'pageSize'>>;

export function useAdminUsers(params: UserListParams) {
  return useQuery({
    queryKey: [...usersKey, 'list', params],
    queryFn: () => api<Paginated<AdminUserListItem>>(`/admin/users${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useAdminUser(id: string) {
  return useQuery({
    queryKey: [...usersKey, 'one', id],
    queryFn: () => orNull(api<AdminUser>(`/admin/users/${id}`)),
  });
}

/** Suspend (with a reason) or reactivate an account; the account page, lists and its audit entries refresh. */
export function useUserStatusChange(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (change: { action: 'suspend'; reason: string } | { action: 'reactivate'; note?: string }) =>
      api<UserStatusResult>(`/admin/users/${id}/${change.action}`, {
        method: 'POST',
        body: change.action === 'suspend' ? { reason: change.reason } : { note: change.note },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: usersKey });
      void queryClient.invalidateQueries({ queryKey: auditKey });
    },
  });
}

const auditKey = ['admin', 'audit'] as const;

type AuditParams = Partial<Pick<AuditLogQuery, 'entityType' | 'entityId' | 'q' | 'page' | 'pageSize'>>;

export function useAuditLog(params: AuditParams) {
  return useQuery({
    queryKey: [...auditKey, params],
    queryFn: () => api<Paginated<AuditLogEntry>>(`/admin/audit-logs${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

/** A provider's checked documents with 5-minute links, for verifying the company */
export function useAdminProviderDocuments(id: string) {
  return useQuery({
    queryKey: [...providersKey, 'documents', id],
    queryFn: () => api<ProviderDocumentInfo[]>(`/admin/providers/${id}/documents`),
    refetchInterval: 4 * 60_000,
  });
}

// ─────────────────────────── Reports (P5-06) ───────────────────────────

const reportsKey = ['admin', 'reports'] as const;

type ReportListParams = Partial<Pick<AdminReportListQuery, 'status' | 'target' | 'page' | 'pageSize'>>;

export function useAdminReports(params: ReportListParams) {
  return useQuery({
    queryKey: [...reportsKey, 'list', params],
    queryFn: () => api<Paginated<AdminReportListItem>>(`/admin/reports${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

/** Open reports, for the menu badge. Only fetched in the admin area. */
export function useOpenReportCount(enabled: boolean) {
  return useQuery({
    queryKey: [...reportsKey, 'open-count'],
    queryFn: () => api<{ count: number }>('/admin/reports/open-count'),
    select: (result) => result.count,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useAdminReport(id: string) {
  return useQuery({
    queryKey: [...reportsKey, 'report', id],
    queryFn: async () => {
      try {
        return await api<AdminReport>(`/admin/reports/${id}`);
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 400)) return null;
        throw error;
      }
    },
  });
}

/** Resolve with an action, or dismiss; the lists, badge and the reported user's page reload. */
export function useReportDecision(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      input: { kind: 'resolve'; data: ResolveReportInput } | { kind: 'dismiss'; data: DismissReportInput },
    ) => api<AdminReport>(`/admin/reports/${id}/${input.kind}`, { method: 'POST', body: input.data }),
    onSuccess: (report) => {
      queryClient.setQueryData([...reportsKey, 'report', id], report);
      void queryClient.invalidateQueries({ queryKey: reportsKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

/** Start-page numbers (P5-07); refreshed every minute while the page is open */
export function useAdminOverview() {
  return useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<AdminOverview>('/admin/overview'),
    refetchInterval: 60_000,
  });
}

/** Start-page charts (UI-05): the last six months and totals by status */
export function useAdminCharts() {
  return useQuery({
    queryKey: ['admin', 'charts'],
    queryFn: () => api<AdminStats>('/admin/stats'),
    staleTime: 5 * 60_000,
  });
}

// Billing (P6-08): needs the finance permission

const plansKey = ['admin', 'plans'] as const;

export function useAdminPlans() {
  return useQuery({ queryKey: plansKey, queryFn: () => api<AdminPlan[]>('/admin/plans') });
}

export function useSavePlan(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PlanInput) => api<AdminPlan>(`/admin/plans/${id}`, { method: 'PUT', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: plansKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'revenue'] });
    },
  });
}

type SubscriptionParams = Partial<
  Pick<AdminSubscriptionListQuery, 'q' | 'status' | 'planId' | 'page' | 'pageSize'>
>;

export function useAdminSubscriptions(params: SubscriptionParams) {
  return useQuery({
    queryKey: ['admin', 'subscriptions', params],
    queryFn: () => api<Paginated<AdminSubscriptionListItem>>(`/admin/subscriptions${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useRevenue() {
  return useQuery({
    queryKey: ['admin', 'revenue'],
    queryFn: () => api<RevenueSummary>('/admin/billing/revenue'),
  });
}
